import { useState, useEffect } from 'react';
import supabase from '../supabase';
import { theme, font, inputStyle, labelStyle, outlineButton } from './adminTheme';
import { useAdminLang } from './AdminLangContext';
import '../design-system.css';

const T = {
    fr: {
        heading: 'Vidéos de campagne',
        year: 'Année',
        titleLabel: 'Titre de la vidéo (optionnel)',
        titlePlaceholder: 'ex: Behind the scenes',
        uploading: 'Upload en cours (peut prendre un moment selon la taille)...',
        existing: (n) => `Vidéos existantes (${n}/3 recommandé)`,
        empty: 'Aucune vidéo pour cette année.',
        untitled: 'Sans titre',
        delete: 'Supprimer',
        selectYearFirst: "Sélectionne une année d'abord.",
        uploadError: (msg) => `Erreur upload : ${msg}`,
        added: 'Vidéo ajoutée ✓',
        confirmDelete: 'Supprimer cette vidéo ?',
    },
    en: {
        heading: 'Campaign videos',
        year: 'Year',
        titleLabel: 'Video title (optional)',
        titlePlaceholder: 'e.g. Behind the scenes',
        uploading: 'Uploading (this can take a moment depending on size)...',
        existing: (n) => `Existing videos (${n}/3 recommended)`,
        empty: 'No videos for this year.',
        untitled: 'Untitled',
        delete: 'Delete',
        selectYearFirst: 'Select a year first.',
        uploadError: (msg) => `Upload error: ${msg}`,
        added: 'Video added ✓',
        confirmDelete: 'Delete this video?',
    },
};

function AdminCampaignVideos() {
    const { lang } = useAdminLang();
    const t = T[lang] || T.fr;
    const [campaigns, setCampaigns] = useState([]);
    const [selectedYear, setSelectedYear] = useState('');
    const [videos, setVideos] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [statusMsg, setStatusMsg] = useState('');
    const [videoTitle, setVideoTitle] = useState('');

    const fetchCampaigns = async () => {
        const { data } = await supabase.from('campaigns').select('year').order('year', { ascending: false });
        if (data) {
            setCampaigns(data);
            if (data.length > 0 && !selectedYear) setSelectedYear(data[0].year);
        }
    };

    const fetchVideos = async (year) => {
        if (!year) return;
        const { data } = await supabase
            .from('campaign_videos')
            .select('*')
            .eq('campaign_year', year)
            .order('order_index', { ascending: true });
        if (data) setVideos(data);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchCampaigns(); }, []);
    useEffect(() => { fetchVideos(selectedYear); }, [selectedYear]);

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!selectedYear) { setStatusMsg(t.selectYearFirst); return; }

        setUploading(true);
        const fileName = `campaign-video-${selectedYear}-${Date.now()}.${file.name.split('.').pop()}`;
        const { error } = await supabase.storage.from('campaign-photos').upload(fileName, file, { upsert: true });

        if (error) {
            setStatusMsg(t.uploadError(error.message));
        } else {
            const { data: urlData } = supabase.storage.from('campaign-photos').getPublicUrl(fileName);
            await supabase.from('campaign_videos').insert({
                campaign_year: Number(selectedYear),
                video_url: urlData.publicUrl,
                title: videoTitle || null,
                order_index: videos.length,
            });
            setVideoTitle('');
            setStatusMsg(t.added);
            fetchVideos(selectedYear);
        }
        setUploading(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t.confirmDelete)) return;
        await supabase.from('campaign_videos').delete().eq('id', id);
        fetchVideos(selectedYear);
    };

    return (
        <div style={{ maxWidth: '480px' }}>
            <p style={{ ...labelStyle, marginBottom: '14px' }}>{t.heading}</p>

            <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>{t.year}</label>
                <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)} style={inputStyle}>
                    {campaigns.map(c => <option key={c.year} value={c.year}>{c.year}</option>)}
                </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>{t.titleLabel}</label>
                <input value={videoTitle} onChange={e => setVideoTitle(e.target.value)} style={{ ...inputStyle, marginBottom: '10px' }} placeholder={t.titlePlaceholder} />
                <input type="file" accept="video/*" onChange={handleUpload} disabled={uploading} style={{ fontSize: '11px', color: theme.textMuted, fontFamily: font }} />
                {uploading && <p style={{ fontSize: '10px', color: theme.textMuted, margin: '4px 0 0' }}>{t.uploading}</p>}
            </div>

            {statusMsg && <p style={{ fontSize: '11px', color: theme.textMuted, fontFamily: font, marginBottom: '16px' }}>{statusMsg}</p>}

            <p style={{ ...labelStyle, marginBottom: '12px' }}>{t.existing(videos.length)}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {videos.length === 0 ? (
                    <p style={{ fontFamily: font, fontSize: '12px', color: theme.textMuted }}>{t.empty}</p>
                ) : videos.map(v => (
                    <div key={v.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: theme.card, border: `1px solid ${theme.border}`, borderRadius: 0 }}>
                        <span style={{ fontFamily: font, fontSize: '12px', color: theme.text }}>{v.title || t.untitled}</span>
                        <button className="wlink" onClick={() => handleDelete(v.id)} style={{ ...outlineButton, fontSize: '10.5px' }}>{t.delete}</button>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default AdminCampaignVideos;